import { FC, ReactNode } from "react";

import {
  Column,
  Paragraph,
  Row,
  Text,
  ChakraTooltip,
  TooltipProps,
  tooltipVariants,
} from "@hightouch/htui";

import { FilterOption } from "src/components/explore/filter-popover/constants";
import { getIconProps } from "src/components/explore/filter-popover/utils";

const MAX_DATA_TOOLTIP_WIDTH = 216;

type DataTooltipProps = {
  children: ReactNode;
  description?: string | null;
  gutter?: number;
  isDisabled?: boolean;
  label: string;
  subtitle?: ReactNode;
  title: FilterOption;
  placement?: TooltipProps["placement"];
};

export const DataTooltip: FC<Readonly<DataTooltipProps>> = ({
  children,
  description,
  gutter = 8,
  isDisabled = false,
  label,
  subtitle,
  title,
  ...props
}) => {
  const { color, icon } = getIconProps(title) ?? {};

  return (
    <ChakraTooltip
      isDisabled={isDisabled}
      openDelay={500}
      motionProps={tooltipVariants}
      gutter={gutter}
      placement="right"
      bg="text.primary"
      borderRadius="xl"
      p={3}
      label={
        <Column
          gap={1}
          minWidth={`${MAX_DATA_TOOLTIP_WIDTH}px`}
          width="min-content"
          fontWeight="normal"
        >
          <Column>
            <Row
              as={Text}
              align="center"
              justify="flex-start"
              color={`${color}.200`}
              fontWeight="semibold"
              gap={1}
              size="sm"
              sx={{ svg: { height: "14px", width: "14px" } }}
            >
              {icon}
              {title}
            </Row>
            {subtitle}
          </Column>
          <Column align="flex-start" sx={{ span: { textAlign: "left" } }}>
            <Text
              color="white"
              fontWeight="medium"
              size="lg"
              wordBreak="break-all"
            >
              {label}
            </Text>
            <Paragraph
              color="base.border"
              size="sm"
              textAlign="start"
              wordBreak="break-word"
            >
              {description}
            </Paragraph>
          </Column>
        </Column>
      }
      {...props}
    >
      {children}
    </ChakraTooltip>
  );
};
